<template>
  <div id="despesas-csv-import-modal-app">
    <wizard-modal
      :show-modal="show"
      :modal-title="t('Importar Despesas CSV')"
      size="large"
      :steps="wizardSteps"
      @step-change="(handleStepChange as any)"
      @finish="handleFinish"
      @cancel="closeModal"
      @close="closeModal">

      <!-- Step 0: File Upload -->
      <template #step-0>
        <box type="primary" :title="t('Upload do Arquivo')">
          <!-- Instructions -->
          <Message severity="info" :closable="false" style="margin-bottom: 1.5rem;">
            <template #icon>
              <i class="pi pi-info-circle" style="font-size: 1.5rem;"></i>
            </template>
            <div style="line-height: 1.6;">
              <strong>{{ t('Instruções:') }}</strong>
              <ul style="margin: 0.5rem 0 0 0; padding-left: 1.5rem;">
                <li>{{ t('O arquivo CSV pode usar vírgula (,) ou ponto e vírgula (;) como separador') }}</li>
                <li>{{ t('O sistema identificará automaticamente valores negativos (despesas)') }}</li>
                <li>{{ t('Valores positivos (recebimentos/créditos) serão ignorados') }}</li>
                <li><strong>{{ t('Formato típico:') }}</strong> {{ t('Data, Histórico, Descrição, Valor') }}</li>
              </ul>
            </div>
          </Message>

          <!-- File Upload Section -->
          <box class="csv-upload-dropzone">
            <div style="text-align: center; padding: 1rem;">
              <i class="pi pi-cloud-upload" style="font-size: 3rem; color: var(--primary-color); margin-bottom: 1rem;"></i>
              <div class="form-group" style="margin-bottom: 0;">
                <label style="display: block; margin-bottom: 0.5rem; font-weight: 600;">
                  {{ t('Selecionar Arquivo CSV') }}
                </label>
                <file-model
                  data-testid="despesas-csv-file-input"
                  :value="csvFile"
                  @input="handleFileUpload">
                </file-model>
              </div>
            </div>
          </box>

          <!-- Upload Progress -->
          <div v-show="uploadProgress > 0 && uploadProgress < 100" style="margin-bottom: 1.5rem;">
            <ProgressBar :value="uploadProgress" :showValue="true"></ProgressBar>
          </div>

          <!-- CSV Preview -->
          <div v-show="csvPreview.length > 0">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
              <h5 style="margin: 0; display: flex; align-items: center; gap: 0.5rem;">
                <i class="pi pi-table"></i>
                {{ t('Preview dos Dados') }}
              </h5>
              <div style="display: flex; gap: 0.5rem;">
                <Chip :label="csvHeaders.length + ' ' + t('colunas')" icon="pi pi-list" />
                <Chip :label="(csvPreview.length) + ' ' + t('linhas')" icon="pi pi-bars" />
              </div>
            </div>

            <div class="table-responsive" style="border: 1px solid var(--surface-border); border-radius: 6px; overflow: hidden;">
              <table class="table table-sm table-bordered" style="margin-bottom: 0;">
                <thead style="background: var(--surface-100);">
                  <tr>
                    <th v-for="header in csvHeaders" :key="header" style="font-weight: 600; padding: 0.75rem;">{{ header }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in csvPreview.slice(0, 5)" :key="index">
                    <td v-for="cell in row" :key="cell" style="padding: 0.5rem;">{{ cell }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style="margin-top: 0.5rem; text-align: center;">
              <small class="text-muted">
                <i class="pi pi-info-circle"></i>
                {{ t('Mostrando as primeiras 5 linhas de') }} {{ csvPreview.length }} {{ t('registros encontrados') }}
              </small>
            </div>
          </div>
        </box>
      </template>

      <!-- Step 1: Field Mapping -->
      <template #step-1>
        <box type="primary" :title="t('Mapeamento de Campos')">
          <!-- Instructions -->
          <Message severity="info" :closable="false" style="margin-bottom: 1.5rem;">
            <template #icon>
              <i class="pi pi-info-circle" style="font-size: 1.5rem;"></i>
            </template>
            <div>
              {{ t('Mapeie as colunas do CSV para os campos da despesa.') }}
              <strong>{{ t('Campos marcados com') }} <span class="text-danger">*</span> {{ t('são obrigatórios.') }}</strong>
            </div>
          </Message>

          <!-- Basic Information Group -->
          <Panel :header="t('Informações Básicas')" :toggleable="false" style="margin-bottom: 1.5rem;">
            <div class="p-fluid">
              <!-- Título -->
              <div class="field" style="margin-bottom: 1.5rem;">
                <label style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; font-weight: 600;">
                  <i class="pi pi-file" style="color: var(--primary-color);"></i>
                  {{ t('Título') }}
                  <Chip label="*" severity="danger" style="height: 1.25rem; font-size: 0.75rem;" />
                </label>
                <select-list
                  elem-id="csv_field_mapping_titulo"
                  data-testid="despesas-csv-mapping-titulo"
                  :itens-select="csvHeaderOptions"
                  :placeholder="t('Selecione a coluna do CSV')"
                  v-model="fieldMapping.titulo">
                </select-list>
                <small class="text-muted" style="display: block; margin-top: 0.25rem;">
                  {{ t('Nome ou identificação principal da despesa') }}
                </small>
              </div>

              <!-- Descrição -->
              <div class="field" style="margin-bottom: 1.5rem;">
                <label style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; font-weight: 600;">
                  <i class="pi pi-align-left" style="color: var(--primary-color);"></i>
                  {{ t('Descrição') }}
                  <Chip :label="t('Opcional')" severity="secondary" style="height: 1.25rem; font-size: 0.75rem;" />
                </label>
                <select-list
                  elem-id="despesas_csv_field_mapping_descricao"
                  data-testid="despesas-csv-mapping-descricao"
                  :itens-select="csvHeaderOptions"
                  :placeholder="t('Selecione a coluna do CSV')"
                  v-model="fieldMapping.descricao">
                </select-list>
                <small class="text-muted" style="display: block; margin-top: 0.25rem;">
                  {{ t('Detalhes adicionais sobre a despesa') }}
                </small>
              </div>

              <Divider />

              <!-- Valor -->
              <div class="field" style="margin-bottom: 1.5rem;">
                <label style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; font-weight: 600;">
                  <i class="pi pi-dollar" style="color: var(--primary-color);"></i>
                  {{ t('Valor') }}
                  <Chip label="*" severity="danger" style="height: 1.25rem; font-size: 0.75rem;" />
                </label>
                <select-list
                  elem-id="despesas_csv_field_mapping_valor"
                  data-testid="despesas-csv-mapping-valor"
                  :itens-select="csvHeaderOptions"
                  :placeholder="t('Selecione a coluna do CSV')"
                  v-model="fieldMapping.valor">
                </select-list>
                <small class="text-muted" style="display: block; margin-top: 0.25rem;">
                  {{ t('Valor monetário da despesa') }}
                </small>
              </div>

              <!-- Data de Vencimento -->
              <div class="field" style="margin-bottom: 0;">
                <label style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; font-weight: 600;">
                  <i class="pi pi-calendar" style="color: var(--primary-color);"></i>
                  {{ t('Data de Vencimento') }}
                  <Chip label="*" severity="danger" style="height: 1.25rem; font-size: 0.75rem;" />
                </label>
                <select-list
                  elem-id="csv_field_mapping_data_vencimento"
                  data-testid="despesas-csv-mapping-data-vencimento"
                  :itens-select="csvHeaderOptions"
                  :placeholder="t('Selecione a coluna do CSV')"
                  v-model="fieldMapping.data_vencimento">
                </select-list>
                <small class="text-muted" style="display: block; margin-top: 0.25rem;">
                  {{ t('Data de vencimento ou lançamento') }}
                </small>
              </div>
            </div>
          </Panel>

          <!-- Configuration Group -->
          <Panel :header="t('Configuração Padrão')" :toggleable="false">
            <div class="p-fluid">
              <!-- Franquia -->
              <div class="field" style="margin-bottom: 0;">
                <label style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; font-weight: 600;">
                  <i class="pi pi-building" style="color: var(--primary-color);"></i>
                  {{ t('Franquia') }}
                </label>
                <select-list
                  elem-id="csv_import_default_franquia"
                  data-testid="despesas-csv-default-franquia"
                  :placeholder="isLoadingFranquias ? t('Carregando franquias...') : t('Selecione a franquia')"
                  :itens-select="franquiasOptions"
                  :disabled="isLoadingFranquias"
                  v-model="defaultFranquiaId">
                </select-list>
                <small class="text-muted" style="display: block; margin-top: 0.25rem;">
                  {{ t('Franquia padrão para todas as despesas importadas') }}
                </small>
              </div>
            </div>
          </Panel>

          <!-- Auto-detection info -->
          <Message v-if="autoDetectedColumns" severity="success" :closable="false" style="margin-top: 1rem;">
            <template #icon>
              <i class="pi pi-check-circle" style="font-size: 1.25rem;"></i>
            </template>
            <div>
              <strong>{{ t('Colunas detectadas automaticamente!') }}</strong>
              {{ t('Verifique se o mapeamento está correto.') }}
            </div>
          </Message>
        </box>
      </template>

      <!-- Step 2: Conciliação (Reconciliation) - Matches RecebimentosCsvImportModal pattern -->
      <template #step-2>
        <box type="primary" :title="t('Conciliação com Despesas Ativas')">
          <!-- Instructions -->
          <Message severity="info" :closable="false" style="margin-bottom: 1.5rem;">
            <template #icon>
              <i class="pi pi-info-circle" style="font-size: 1.5rem;"></i>
            </template>
            <div>
              {{ t('O sistema encontrou as correspondências abaixo baseado no valor.') }}
              {{ t('Revise e ajuste conforme necessário. Despesas marcadas serão baixadas.') }}
            </div>
          </Message>

          <!-- Loading state -->
          <div v-if="isLoadingAtivoExpenses" style="text-align: center; padding: 2rem;">
            <ProgressSpinner style="width: 3rem; height: 3rem;" />
            <p style="margin-top: 1rem;">{{ t('Buscando despesas ativas para conciliação...') }}</p>
          </div>

          <!-- Summary Cards -->
          <div v-if="!isLoadingAtivoExpenses" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 0.75rem; margin-bottom: 1.5rem;">
            <!-- Exact Confidence -->
            <box class="summary-stat-card stat-exact" v-tooltip.top="t('Valor e título coincidem exatamente')">
              <div style="display: flex; align-items: center; gap: 0.75rem;">
                <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: var(--cyan-600); border-radius: 50%;">
                  <i class="pi pi-verified" style="font-size: 1.25rem; color: white;"></i>
                </div>
                <div>
                  <div style="font-size: 0.75rem; color: var(--cyan-700); font-weight: 500;">{{ t('EXATA') }}</div>
                  <div style="font-size: 1.5rem; font-weight: 700; color: var(--cyan-800);">{{ exactMatchCount }}</div>
                </div>
              </div>
            </box>

            <!-- High Confidence -->
            <box class="summary-stat-card stat-high">
              <div style="display: flex; align-items: center; gap: 0.75rem;">
                <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: var(--green-500); border-radius: 50%;">
                  <i class="pi pi-check" style="font-size: 1.25rem; color: white;"></i>
                </div>
                <div>
                  <div style="font-size: 0.75rem; color: var(--green-700); font-weight: 500;">{{ t('ALTA') }}</div>
                  <div style="font-size: 1.5rem; font-weight: 700; color: var(--green-800);">{{ highMatchCount }}</div>
                </div>
              </div>
            </box>

            <!-- Medium Confidence -->
            <box class="summary-stat-card stat-medium">
              <div style="display: flex; align-items: center; gap: 0.75rem;">
                <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: var(--yellow-500); border-radius: 50%;">
                  <i class="pi pi-exclamation-triangle" style="font-size: 1.25rem; color: white;"></i>
                </div>
                <div>
                  <div style="font-size: 0.75rem; color: var(--yellow-700); font-weight: 500;">{{ t('MÉDIA') }}</div>
                  <div style="font-size: 1.5rem; font-weight: 700; color: var(--yellow-800);">{{ mediumMatchCount }}</div>
                </div>
              </div>
            </box>

            <!-- No Match -->
            <box class="summary-stat-card stat-none">
              <div style="display: flex; align-items: center; gap: 0.75rem;">
                <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: var(--red-500); border-radius: 50%;">
                  <i class="pi pi-question" style="font-size: 1.25rem; color: white;"></i>
                </div>
                <div>
                  <div style="font-size: 0.75rem; color: var(--red-700); font-weight: 500;">{{ t('SEM MATCH') }}</div>
                  <div style="font-size: 1.5rem; font-weight: 700; color: var(--red-800);">{{ noMatchCount }}</div>
                </div>
              </div>
            </box>

            <!-- Ativo Expenses Available -->
            <box class="summary-stat-card stat-ativo" v-tooltip.top="t('Despesas com status Ativo disponíveis para conciliação')">
              <div style="display: flex; align-items: center; gap: 0.75rem;">
                <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: var(--gray-500); border-radius: 50%;">
                  <i class="pi pi-clock" style="font-size: 1.25rem; color: white;"></i>
                </div>
                <div>
                  <div style="font-size: 0.75rem; color: var(--gray-700); font-weight: 500;">{{ t('ATIVAS') }}</div>
                  <div style="font-size: 1.5rem; font-weight: 700; color: var(--gray-800);">{{ ativoExpenses.length }}</div>
                </div>
              </div>
            </box>
          </div>

          <!-- Matching Table -->
          <div v-if="!isLoadingAtivoExpenses" class="table-responsive" style="max-height: 400px; overflow-y: auto;">
            <table class="table table-sm table-bordered">
              <thead>
                <tr>
                  <th style="width: 50px;">{{ t('#') }}</th>
                  <th style="min-width: 100px;">{{ t('Data no CSV') }}</th>
                  <th style="min-width: 120px;">{{ t('Valor no CSV') }}</th>
                  <th style="min-width: 150px;">{{ t('Descrição no CSV') }}</th>
                  <th style="min-width: 250px;">{{ t('Despesa Sugerida') }}</th>
                  <th style="width: 100px;">{{ t('Confiança') }}</th>
                  <th style="width: 80px; text-align: center;">{{ t('Ação') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(record, index) in processedRecords" :key="index"
                    :class="getRowClass(record)">
                  <td>{{ index + 1 }}</td>
                  <td>{{ formatDateForDisplay(record.data_vencimento) }}</td>
                  <td>{{ n(record.valor, 'currency') }}</td>
                  <td class="description-cell">{{ record.descricao || record.titulo || '-' }}</td>
                  <td>
                    <template v-if="record.ativoMatches && record.ativoMatches.length > 0">
                      <select-list
                        :elem-id="'csv_ativo_match_' + index"
                        :data-testid="'despesas-csv-ativo-match-' + index"
                        :itens-select="getMatchOptions(record)"
                        :placeholder="t('Selecione...')"
                        v-model="record.selectedAtivoId"
                        @update:modelValue="handleMatchSelection(record, $event)">
                      </select-list>
                    </template>
                    <template v-else>
                      <span class="text-danger">{{ t('Nenhuma correspondência') }}</span>
                    </template>
                  </td>
                  <td style="text-align: center;">
                    <Tag v-if="record.confidence === 'EXACT'" severity="info" :value="t('Exata')" />
                    <Tag v-else-if="record.confidence === 'HIGH'" severity="success" :value="t('Alta')" />
                    <Tag v-else-if="record.confidence === 'MEDIUM'" severity="warn" :value="t('Média')" />
                    <Tag v-else severity="secondary" :value="t('Nenhuma')" />
                  </td>
                  <td style="text-align: center;">
                    <Button
                      :icon="record.checked ? 'pi pi-times' : 'pi pi-plus'"
                      :data-testid="'despesas-csv-toggle-baixa-' + index"
                      :severity="record.checked ? 'danger' : 'success'"
                      size="small"
                      text
                      rounded
                      :disabled="!record.selectedAtivoId"
                      @click="toggleRowChecked(record)"
                      v-tooltip.top="record.checked ? t('Remover da baixa') : t('Marcar para baixar')">
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </box>
      </template>

      <!-- Step 3: Novas Despesas (New Expenses Review) -->
      <template #step-3>
        <box type="primary" :title="t('Novas Despesas')">
          <!-- Instructions -->
          <Message severity="info" :closable="false" style="margin-bottom: 1.5rem;">
            <template #icon>
              <i class="pi pi-info-circle" style="font-size: 1.5rem;"></i>
            </template>
            <div>
              {{ t('Revise e edite os campos das novas despesas.') }}
              {{ t('Você pode alterar valores, categoria e remover linhas indesejadas.') }}
            </div>
          </Message>

          <!-- Loading indicator for category suggestions -->
          <Message v-if="isSuggestingCategories" severity="warn" :closable="false" style="margin-bottom: 1.5rem;">
            <template #icon>
              <ProgressSpinner style="width: 1.5rem; height: 1.5rem" strokeWidth="4" />
            </template>
            <div>
              {{ t('Buscando categorias sugeridas baseadas em despesas anteriores...') }}
            </div>
          </Message>

          <!-- Success message for auto-suggested categories -->
          <Message v-if="suggestedCategoryCountForCreation > 0 && !isSuggestingCategories" severity="success" :closable="false" style="margin-bottom: 1.5rem;">
            <template #icon>
              <i class="pi pi-sparkles" style="font-size: 1.5rem;"></i>
            </template>
            <div>
              <strong>{{ suggestedCategoryCountForCreation }}</strong> {{ t('categorias foram sugeridas automaticamente') }}
              <i class="pi pi-sparkles" style="color: var(--purple-500);"></i>
              {{ t('baseadas em despesas similares. Verifique e ajuste conforme necessário.') }}
            </div>
          </Message>

          <!-- No records to create message -->
          <Message v-if="recordsForCreation.length === 0" severity="warn" :closable="false" style="margin-bottom: 1.5rem;">
            <template #icon>
              <i class="pi pi-info-circle" style="font-size: 1.5rem;"></i>
            </template>
            <div>
              {{ t('Todos os registros do CSV foram conciliados com despesas existentes.') }}
              {{ t('Não há novas despesas para criar.') }}
            </div>
          </Message>

          <!-- Summary Cards -->
          <div v-if="recordsForCreation.length > 0" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1.5rem;">
            <!-- Valid Records Card -->
            <box class="creation-summary-card stat-valid">
              <div style="display: flex; align-items: center; gap: 1rem;">
                <div style="flex-shrink: 0; width: 3rem; height: 3rem; display: flex; align-items: center; justify-content: center; background: var(--green-500); border-radius: 50%;">
                  <i class="pi pi-check" style="font-size: 1.5rem; color: white;"></i>
                </div>
                <div style="flex: 1;">
                  <div style="font-size: 0.875rem; color: var(--green-700); font-weight: 500; margin-bottom: 0.25rem;">
                    {{ t('VÁLIDOS') }}
                  </div>
                  <div style="font-size: 2rem; font-weight: 700; color: var(--green-800); line-height: 1;">
                    {{ validRecordsForCreation }}
                  </div>
                </div>
              </div>
            </box>

            <!-- Invalid Records Card -->
            <box class="creation-summary-card stat-invalid">
              <div style="display: flex; align-items: center; gap: 1rem;">
                <div style="flex-shrink: 0; width: 3rem; height: 3rem; display: flex; align-items: center; justify-content: center; background: var(--red-500); border-radius: 50%;">
                  <i class="pi pi-times" style="font-size: 1.5rem; color: white;"></i>
                </div>
                <div style="flex: 1;">
                  <div style="font-size: 0.875rem; color: var(--red-700); font-weight: 500; margin-bottom: 0.25rem;">
                    {{ t('INVÁLIDOS') }}
                  </div>
                  <div style="font-size: 2rem; font-weight: 700; color: var(--red-800); line-height: 1;">
                    {{ invalidRecordsForCreation }}
                  </div>
                </div>
              </div>
            </box>

            <!-- Total New Records Card -->
            <box class="creation-summary-card stat-new">
              <div style="display: flex; align-items: center; gap: 1rem;">
                <div style="flex-shrink: 0; width: 3rem; height: 3rem; display: flex; align-items: center; justify-content: center; background: var(--blue-500); border-radius: 50%;">
                  <i class="pi pi-plus" style="font-size: 1.5rem; color: white;"></i>
                </div>
                <div style="flex: 1;">
                  <div style="font-size: 0.875rem; color: var(--blue-700); font-weight: 500; margin-bottom: 0.25rem;">
                    {{ t('NOVAS') }}
                  </div>
                  <div style="font-size: 2rem; font-weight: 700; color: var(--blue-800); line-height: 1;">
                    {{ recordsForCreation.length }}
                  </div>
                </div>
              </div>
            </box>

            <!-- Auto-Suggested Categories Card -->
            <box v-if="suggestedCategoryCountForCreation > 0"
                 class="creation-summary-card stat-suggested"
                 v-tooltip.top="t('Categorias sugeridas automaticamente baseadas em despesas similares')">
              <div style="display: flex; align-items: center; gap: 1rem;">
                <div style="flex-shrink: 0; width: 3rem; height: 3rem; display: flex; align-items: center; justify-content: center; background: var(--purple-500); border-radius: 50%;">
                  <i class="pi pi-sparkles" style="font-size: 1.5rem; color: white;"></i>
                </div>
                <div style="flex: 1;">
                  <div style="font-size: 0.875rem; color: var(--purple-700); font-weight: 500; margin-bottom: 0.25rem;">
                    {{ t('SUGERIDAS') }}
                  </div>
                  <div style="font-size: 2rem; font-weight: 700; color: var(--purple-800); line-height: 1;">
                    {{ suggestedCategoryCountForCreation }}
                  </div>
                </div>
              </div>
            </box>
          </div>

          <!-- Records Table -->
          <div v-if="recordsForCreation.length > 0" class="table-responsive" style="max-height: 400px; overflow-y: auto;">
            <table class="table table-sm table-bordered">
              <thead>
                <tr>
                  <th style="width: 50px;">{{ t('Linha') }}</th>
                  <th style="min-width: 200px;">{{ t('Título') }}</th>
                  <th style="min-width: 150px;">{{ t('Descrição') }}</th>
                  <th style="min-width: 120px;">{{ t('Valor') }}</th>
                  <th style="min-width: 150px;">{{ t('Categoria') }}</th>
                  <th style="width: 80px;">{{ t('Válido') }}</th>
                  <th style="width: 80px;">{{ t('Ações') }}</th>
                  <th style="min-width: 120px;">{{ t('Data') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(record, index) in recordsForCreation" :key="index"
                    :class="{ 'table-danger': !record.isValid, 'table-success': record.isValid }">
                  <td>{{ index + 1 }}</td>
                  <td>
                    <input-text
                      :elem-id="'csv_record_titulo_' + index"
                      :data-testid="'despesas-csv-record-titulo-' + index"
                      v-model="record.titulo"
                      :placeholder="t('Título')"
                      size="small"
                      fluid>
                    </input-text>
                  </td>
                  <td>
                    <input-text
                      :elem-id="'csv_record_descricao_' + index"
                      :data-testid="'despesas-csv-record-descricao-' + index"
                      v-model="record.descricao"
                      :placeholder="t('Descrição')"
                      size="small"
                      fluid>
                    </input-text>
                  </td>
                  <td>
                    <input-number
                      :elem-id="'csv_record_valor_' + index"
                      :data-testid="'despesas-csv-record-valor-' + index"
                      v-model="record.valor"
                      :placeholder="t('Valor')"
                      mode="currency"
                      currency="BRL"
                      locale="pt-BR">
                    </input-number>
                  </td>
                  <td>
                    <div style="display: flex; align-items: center; gap: 0.25rem;">
                      <select-list
                        :elem-id="'csv_record_categoria_' + index"
                        :data-testid="'despesas-csv-record-categoria-' + index"
                        :itens-select="categoriasOptions"
                        v-model="record.categoria_id"
                        style="flex: 1;">
                      </select-list>
                      <i v-if="record.categorySuggested"
                         class="pi pi-sparkles"
                         style="color: var(--purple-500); font-size: 1rem; cursor: help;"
                         v-tooltip.top="t('Sugerido automaticamente') + ': ' + record.categoryMatchedWith + ' (' + Math.round(record.categoryConfidence * 100) + '%)'">
                      </i>
                    </div>
                  </td>
                  <td style="text-align: center;">
                    <Chip
                      v-if="record.isValid"
                      :label="t('Válido')"
                      icon="pi pi-check"
                      severity="success"
                      style="font-size: 0.75rem;" />
                    <Chip
                      v-else
                      :label="t('Inválido')"
                      icon="pi pi-times"
                      severity="danger"
                      style="font-size: 0.75rem;"
                      :title="record.errors.join(', ')" />
                  </td>
                  <td style="text-align: center;">
                    <Button
                      icon="pi pi-trash"
                      :data-testid="'despesas-csv-record-delete-' + index"
                      severity="danger"
                      size="small"
                      text
                      rounded
                      @click="deleteRecordForCreation(index)"
                      :title="t('Remover linha')">
                    </Button>
                  </td>
                  <td style="text-align: center; color: var(--text-color-secondary);">
                    {{ formatDateForDisplay(record.data_vencimento) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </box>
      </template>

      <!-- Step 4: Import Progress -->
      <template #step-4>
        <box type="primary" :title="t('Importando Dados')">
          <div class="progress">
            <div class="progress-bar progress-bar-striped active"
                 role="progressbar"
                 :style="'width: ' + importProgress + '%'">
              {{ importProgress }}%
            </div>
          </div>

          <div class="row" style="margin-top: 20px;">
            <div class="col-md-3">
              <div class="info-box bg-blue">
                <span class="info-box-icon"><ProgressSpinner style="width: 1.5rem; height: 1.5rem" strokeWidth="4" /></span>
                <div class="info-box-content">
                  <span class="info-box-text">{{ t('Processados') }}</span>
                  <span class="info-box-number">{{ importStats.processed }}</span>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="info-box bg-green">
                <span class="info-box-icon"><i class="pi pi-check"></i></span>
                <div class="info-box-content">
                  <span class="info-box-text">{{ t('Sucessos') }}</span>
                  <span class="info-box-number">{{ importStats.success }}</span>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="info-box bg-red">
                <span class="info-box-icon"><i class="pi pi-times"></i></span>
                <div class="info-box-content">
                  <span class="info-box-text">{{ t('Erros') }}</span>
                  <span class="info-box-number">{{ importStats.errors }}</span>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="info-box bg-cyan" style="background-color: #00bcd4 !important; color: white;">
                <span class="info-box-icon"><i class="pi pi-sync"></i></span>
                <div class="info-box-content">
                  <span class="info-box-text">{{ t('Baixados') }}</span>
                  <span class="info-box-number">{{ importStats.reconciled }}</span>
                </div>
              </div>
            </div>
          </div>
        </box>
      </template>
    </wizard-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';
import { useDespesas } from '../../composables/useDespesas';
import type { DespesaListItem } from '../../types/despesas.types';
import WizardModal from '../../ui/WizardModal.vue';

interface SelectOption {
  ID: string;
  Name: string;
  disabled?: boolean;
}

interface FieldMapping {
  titulo: string;
  descricao: string;
  valor: string;
  data_vencimento: string;
}

interface ImportStats {
  processed: number;
  success: number;
  errors: number;
  reconciled: number;
}

interface AtivoMatch {
  expense: DespesaListItem;
  expenseId: string;
  expenseTitle: string;
  expenseValor: number;
  confidence: number;
  confidenceLevel: 'EXACT' | 'HIGH' | 'MEDIUM' | 'NONE';
}

interface CsvRecord {
  titulo: string;
  descricao: string;
  valor: number;
  originalValueNegative: boolean;
  data_vencimento: string;
  franquia_id: string;
  status_id: string;
  categoria_id: string;
  qtd_parcelas: number;
  errors: string[];
  isValid: boolean;
  categorySuggested: boolean;
  categoryConfidence: number;
  categoryMatchedWith: string;
  ativoMatches: AtivoMatch[];
  selectedAtivoId: string | null;
  selectedMatch: AtivoMatch | null;
  confidence: 'EXACT' | 'HIGH' | 'MEDIUM' | 'NONE';
  checked: boolean;
}

interface MatchedRecord {
  csvRecord: CsvRecord;
  existingExpense: DespesaListItem;
  confidence: number;
  confirmed: boolean;
}

interface CategoryMatch {
  categoryId: string;
  confidence: number;
  matchedWith: string;
}

interface WizardStep {
  title: string;
  description: string;
  validate: () => boolean;
}

// Get i18n composable
const { t, n, d } = useI18n();

// Get filtro composable
const { getSelectedFranchisesSelectArray } = useFiltro();

// Get despesas composable
const { fetchCategorias, fetchDespesasWithOverride, saveDespesa: saveDespesaAPI, baixarDespesa } = useDespesas();

// Define emits
const emit = defineEmits(['close']);

// Reactive data
const show = ref<boolean>(false);
const csvFile = ref<File | null>(null);
const csvContent = ref<string>('');
const csvHeaders = ref<string[]>([]);
const csvPreview = ref<string[][]>([]);
const uploadProgress = ref<number>(0);

const fieldMapping = reactive<FieldMapping>({
  titulo: '',
  descricao: '',
  valor: '',
  data_vencimento: ''
});

const defaultFranquiaId = ref<string>('');
const defaultStatus = ref<string>('1');
const defaultCategoria = ref<string>('');
const defaultQtdParcelas = ref<number>(1);

const processedRecords = ref<CsvRecord[]>([]);

const franquiasOptions = ref<SelectOption[]>([]);
const categoriasOptions = ref<SelectOption[]>([]);

const isLoadingFranquias = ref<boolean>(false);

const importProgress = ref<number>(0);
const importStats = reactive<ImportStats>({
  processed: 0,
  success: 0,
  errors: 0,
  reconciled: 0
});
const importComplete = ref<boolean>(false);
const autoDetectedColumns = ref<boolean>(false);
const skippedPositiveCount = ref<number>(0);

// Reconciliation state
const ativoExpenses = ref<DespesaListItem[]>([]);
const matchedRecords = ref<MatchedRecord[]>([]);
const isLoadingAtivoExpenses = ref<boolean>(false);
const reconciliationComplete = ref<boolean>(false);

// Wizard steps configuration
const wizardSteps = computed<WizardStep[]>(() => [
  {
    title: t('Upload do Arquivo'),
    description: t('Selecione e visualize o arquivo CSV'),
    validate: () => csvPreview.value.length > 0
  },
  {
    title: t('Mapeamento de Campos'),
    description: t('Mapeie as colunas do CSV para os campos da despesa'),
    validate: () => canProceedToReview.value
  },
  {
    title: t('Conciliação'),
    description: t('Identificar despesas existentes para baixar'),
    validate: () => true // Can always proceed (even with 0 matches)
  },
  {
    title: t('Novas Despesas'),
    description: t('Revise os dados das novas despesas'),
    validate: () => recordsForCreation.value.length === 0 || validRecordsForCreation.value > 0
  },
  {
    title: t('Importação'),
    description: t('Importando despesas'),
    validate: () => true // Always valid, it's progress screen
  }
]);

// Computed options that depend on i18n
const statusOptions = computed(() => [
  { ID: '1', Name: t('Ativo') },
  { ID: '2', Name: t('Cancelado') },
  { ID: '3', Name: t('Baixado') }
]);

// Computed properties
const csvHeaderOptions = computed(() => {
  return [
    { ID: '', Name: t('Selecionar') },
    ...csvHeaders.value.map((header, index) => ({
      ID: index.toString(),
      Name: header
    }))
  ];
});

const canProceedToReview = computed(() => {
  return fieldMapping.titulo !== '' && 
         fieldMapping.valor !== '' && 
         fieldMapping.data_vencimento !== '' &&
         defaultFranquiaId.value !== '';
});

const validRecords = computed(() => {
  return processedRecords.value.filter(record => record.isValid).length;
});

const invalidRecords = computed(() => {
  return processedRecords.value.filter(record => !record.isValid).length;
});

const totalRecords = computed(() => {
  return processedRecords.value.length;
});

const suggestedCategoryCount = computed(() => {
  return processedRecords.value.filter(record => record.categorySuggested).length;
});

// Records that will be created (not marked for baixar)
const recordsForCreation = computed(() => {
  return processedRecords.value.filter(record => !record.checked);
});

const validRecordsForCreation = computed(() => {
  return recordsForCreation.value.filter(record => record.isValid).length;
});

const invalidRecordsForCreation = computed(() => {
  return recordsForCreation.value.filter(record => !record.isValid).length;
});

// Records matched for reconciliation (to be baixar) - matches RecebimentosCsvImportModal pattern
const recordsForReconciliation = computed(() => {
  return processedRecords.value
    .filter(record => record.checked && record.selectedAtivoId)
    .map(record => {
      // First try to find in ativoMatches (auto-matched)
      const selectedMatch = record.ativoMatches?.find(m => m.expenseId === record.selectedAtivoId);
      let expense: DespesaListItem | null = selectedMatch ? selectedMatch.expense : null;

      // If not in ativoMatches, find in all ativoExpenses (manual selection)
      if (!expense) {
        expense = ativoExpenses.value.find(e =>
          (e.despesa_id || e.id) === record.selectedAtivoId
        ) ?? null;
      }

      return {
        csvRecord: record,
        existingExpense: expense,
        confidence: selectedMatch ? selectedMatch.confidence : 1.0,
        confirmed: true
      };
    })
    .filter(r => r.existingExpense); // Ensure expense exists
});

const suggestedCategoryCountForCreation = computed(() => {
  return recordsForCreation.value.filter(record => record.categorySuggested).length;
});

// Match count computed properties for Conciliação step - matches RecebimentosCsvImportModal pattern
const exactMatchCount = computed(() => {
  return processedRecords.value.filter(r => r.confidence === 'EXACT' && r.checked).length;
});

const highMatchCount = computed(() => {
  return processedRecords.value.filter(r => r.confidence === 'HIGH' && r.checked).length;
});

const mediumMatchCount = computed(() => {
  return processedRecords.value.filter(r => r.confidence === 'MEDIUM' && r.checked).length;
});

const noMatchCount = computed(() => {
  return processedRecords.value.filter(r => !r.checked).length;
});

// Wizard event handlers
const handleStepChange = async (event: { from: number; to: number }) => {
  log('DespesasCsvImportModal', 'handleStepChange', 'Step changed', event);

  // event = { from: oldStep, to: newStep }
  if (event.to === 2) {
    // Entering Conciliação step - process records and find matches with Ativo expenses
    await processRecords();
    await findMatchesWithAtivoExpenses();
  } else if (event.to === 3) {
    // Entering Novas Despesas step - apply category suggestions to unmatched records
    await suggestCategoriesFromHistory(recordsForCreation.value);
    // Revalidate records for creation
    validateRecordsForCreation();
  } else if (event.to === 4) {
    // Entering import step - start import (both updates and creates)
    startImport();
  }
};

const handleFinish = () => {
  log('DespesasCsvImportModal', 'handleFinish', 'Wizard finished');
  // Import complete, close modal
  closeModal();
};

// Record management methods
const deleteRecord = (index: number) => {
  log('DespesasCsvImportModal', 'deleteRecord', 'Deleting record at index', index);
  processedRecords.value.splice(index, 1);
};

/**
 * Delete a record from the creation list
 * Finds the actual record in processedRecords and removes it
 */
const deleteRecordForCreation = (index: number) => {
  log('DespesasCsvImportModal', 'deleteRecordForCreation', 'Deleting record at index', index);
  const recordToDelete = recordsForCreation.value[index];
  if (recordToDelete) {
    const actualIndex = processedRecords.value.indexOf(recordToDelete);
    if (actualIndex !== -1) {
      processedRecords.value.splice(actualIndex, 1);
    }
  }
};

const openModal = () => {
  log('DespesasCsvImportModal', 'openModal', 'Open');
  resetForm();
  show.value = true;
  // Load options when modal opens
  loadOptions();
};

const closeModal = () => {
  log('DespesasCsvImportModal', 'closeModal', 'Close');
  show.value = false;
  resetForm();
  
  if (importStats.success > 0) {
    eventBus.emit('despesa.csv.imported');
  }
  
  // Notify parent to hide modal (unmount via v-if)
  emit('close');
};

const resetForm = () => {
  log('DespesasCsvImportModal', 'resetForm', 'Reset form');
  show.value = false;
  csvFile.value = null;
  csvContent.value = '';
  csvHeaders.value = [];
  csvPreview.value = [];
  uploadProgress.value = 0;
  Object.assign(fieldMapping, {
    titulo: '',
    descricao: '',
    valor: '',
    data_vencimento: ''
  });
  autoDetectedColumns.value = false;
  skippedPositiveCount.value = 0;
  processedRecords.value = [];
  importProgress.value = 0;
  Object.assign(importStats, { processed: 0, success: 0, errors: 0, reconciled: 0 });
  importComplete.value = false;
  // Reset auto-categorization state
  historicalExpenses.value = [];
  isSuggestingCategories.value = false;
  // Reset reconciliation state
  ativoExpenses.value = [];
  matchedRecords.value = [];
  isLoadingAtivoExpenses.value = false;
  reconciliationComplete.value = false;
};

const handleFileUpload = (file: File | null) => {
  log('DespesasCsvImportModal', 'handleFileUpload', 'File present?', !!file);
  if (!file) return;

  csvFile.value = file;
  uploadProgress.value = 0;

  const reader = new FileReader();
  reader.onload = (e: ProgressEvent<FileReader>) => {
    csvContent.value = String(e.target?.result ?? '');
    parseCSV();
  };

  reader.onprogress = (e: ProgressEvent<FileReader>) => {
    if (e.lengthComputable) {
      uploadProgress.value = Math.round((e.loaded / e.total) * 100);
    }
  };

  reader.readAsText(file);
};

const parseCSV = () => {
  log('DespesasCsvImportModal', 'parseCSV', 'Begin');
  const lines = csvContent.value.split('\n').filter(line => line.trim());
  if (lines.length === 0) return;

  // Detect separator
  const firstLine = lines[0];
  const separator = firstLine.includes(';') ? ';' : ',';

  // Parse headers
  csvHeaders.value = firstLine.split(separator).map(header => header.trim().replace(/"/g, ''));

  // Parse preview data
  csvPreview.value = lines.slice(1, 6).map(line =>
    line.split(separator).map(cell => cell.trim().replace(/"/g, ''))
  );

  uploadProgress.value = 100;

  // Auto-detect column mappings
  autoDetectColumnMappings();
};

/**
 * Auto-detect column mappings based on header names and data patterns
 */
const autoDetectColumnMappings = () => {
  log('DespesasCsvImportModal', 'autoDetectColumnMappings', 'Begin', csvHeaders.value);

  // Reset auto-detection flag
  autoDetectedColumns.value = false;

  // Patterns for detecting columns (case-insensitive)
  const tituloPatterns = [
    /^titulo$/i, /^título$/i, /^title$/i, /^nome$/i, /^name$/i,
    /^historico$/i, /^histórico$/i, /^hist$/i,
    /favorecido/i, /beneficiario/i, /beneficiário/i,
    /^razao/i, /^razão/i, /fornecedor/i
  ];

  const descricaoPatterns = [
    /^descricao$/i, /^descrição$/i, /^desc$/i, /^description$/i,
    /memo/i, /observacao/i, /observação/i, /^obs$/i,
    /detalhe/i, /detail/i, /complemento/i
  ];

  const valorPatterns = [
    /^valor$/i, /^value$/i, /^val$/i, /valor_/i,
    /montante/i, /quantia/i, /saldo/i, /total/i,
    /credito/i, /crédito/i, /debito/i, /débito/i,
    /amount/i, /^vlr$/i, /price/i, /preco/i, /preço/i
  ];

  const datePatterns = [
    /^data$/i, /^date$/i, /^dt$/i, /^dt_/i, /data_/i,
    /vencimento/i, /lancamento/i, /lançamento/i, /movimento/i,
    /^when$/i, /^dia$/i, /^mes$/i
  ];

  let detectedTitulo: string | null = null;
  let detectedDescricao: string | null = null;
  let detectedValor: string | null = null;
  let detectedDate: string | null = null;

  // First pass: match by header name patterns
  csvHeaders.value.forEach((header, index) => {
    const headerNormalized = header.toLowerCase().trim();
    const indexStr = index.toString();

    // Check for titulo column
    if (!detectedTitulo) {
      for (const pattern of tituloPatterns) {
        if (pattern.test(headerNormalized)) {
          detectedTitulo = indexStr;
          log('DespesasCsvImportModal', 'autoDetect', 'Found titulo column by name', { header, index });
          break;
        }
      }
    }

    // Check for descricao column
    if (!detectedDescricao) {
      for (const pattern of descricaoPatterns) {
        if (pattern.test(headerNormalized)) {
          detectedDescricao = indexStr;
          log('DespesasCsvImportModal', 'autoDetect', 'Found descricao column by name', { header, index });
          break;
        }
      }
    }

    // Check for valor column
    if (!detectedValor) {
      for (const pattern of valorPatterns) {
        if (pattern.test(headerNormalized)) {
          detectedValor = indexStr;
          log('DespesasCsvImportModal', 'autoDetect', 'Found valor column by name', { header, index });
          break;
        }
      }
    }

    // Check for date column
    if (!detectedDate) {
      for (const pattern of datePatterns) {
        if (pattern.test(headerNormalized)) {
          detectedDate = indexStr;
          log('DespesasCsvImportModal', 'autoDetect', 'Found date column by name', { header, index });
          break;
        }
      }
    }
  });

  // Second pass: if not found by name, try to detect by data patterns
  if (csvPreview.value.length > 0 && (!detectedDate || !detectedValor)) {
    const sampleRows = csvPreview.value.slice(0, 5); // Check first 5 rows

    csvHeaders.value.forEach((header, index) => {
      if (detectedDate && detectedValor) return; // Already found both

      const indexStr = index.toString();
      const sampleValues = sampleRows.map(row => row[index]).filter(v => v);

      // Try to detect date by data format (DD/MM/YYYY, YYYY-MM-DD, etc)
      if (!detectedDate) {
        const dateRegex = /^(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}|\d{4}[\/\-\.]\d{1,2}[\/\-\.]\d{1,2})$/;
        const dateMatches = sampleValues.filter(v => dateRegex.test(v.trim()));
        if (dateMatches.length >= Math.min(3, sampleValues.length)) {
          detectedDate = indexStr;
          log('DespesasCsvImportModal', 'autoDetect', 'Found date column by data pattern', { header, index });
        }
      }

      // Try to detect value by data format (numbers with comma/period decimals)
      if (!detectedValor) {
        const valueRegex = /^-?\s*[R$]?\s*[\d.,]+\s*$/;
        const valueMatches = sampleValues.filter(v => valueRegex.test(v.trim()));
        if (valueMatches.length >= Math.min(3, sampleValues.length)) {
          // Additional check: parse as number
          const parsed = sampleValues.map(v => {
            const cleaned = v.replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
            return parseFloat(cleaned);
          });
          const validNumbers = parsed.filter(n => !isNaN(n) && n !== 0);
          if (validNumbers.length >= Math.min(2, sampleValues.length)) {
            detectedValor = indexStr;
            log('DespesasCsvImportModal', 'autoDetect', 'Found valor column by data pattern', { header, index });
          }
        }
      }
    });
  }

  // Apply detected mappings
  if (detectedTitulo) {
    fieldMapping.titulo = detectedTitulo;
  }
  if (detectedDescricao) {
    fieldMapping.descricao = detectedDescricao;
  }
  if (detectedValor) {
    fieldMapping.valor = detectedValor;
  }
  if (detectedDate) {
    fieldMapping.data_vencimento = detectedDate;
  }

  // Set flag if at least required fields were detected
  autoDetectedColumns.value = !!(detectedTitulo && detectedValor && detectedDate);

  log('DespesasCsvImportModal', 'autoDetectColumnMappings', 'Result', {
    titulo: detectedTitulo,
    descricao: detectedDescricao,
    valor: detectedValor,
    date: detectedDate,
    autoDetected: autoDetectedColumns.value
  });
};

const loadOptions = async () => {
  log('DespesasCsvImportModal', 'loadOptions', 'Begin');
  try {
    // Load franquias from eventbus
    await loadFranquiasFromFiltroService();

    // Load categorias
    const categoriasData = await fetchCategorias();

    if (categoriasData) {
      categoriasOptions.value = formatCategoriasData(categoriasData);
      log('DespesasCsvImportModal', 'loadOptions', 'Loaded categorias', categoriasOptions.value.length);

      // Set first categoria as default if none is selected
      if (!defaultCategoria.value && categoriasOptions.value.length > 0) {
        defaultCategoria.value = categoriasOptions.value[0].ID;
      }
    }

  } catch (error) {
    console.error('Error loading options:', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar opções: ') + message);
  }
};

const loadFranquiasFromFiltroService = async () => {
  log('DespesasCsvImportModal', 'loadFranquiasFromFiltroService', 'Requesting franquias from filtro service');
  try {
    const franquias = await getSelectedFranchisesSelectArray();
    franquiasOptions.value = formatFranquiasData(franquias || []);
    log('DespesasCsvImportModal', 'loadFranquiasFromFiltroService', 'Received franquias', (franquias || []).length);
    return franquias || [];
  } catch (error) {
    log('DespesasCsvImportModal', 'loadFranquiasFromFiltroService', 'Error loading franquias', error);
    franquiasOptions.value = [];
    return [];
  }
};

const formatFranquiasData = (data: any): SelectOption[] => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((item: any): SelectOption => {
    if (typeof item === 'string') {
      return { ID: item, Name: item };
    } else if (typeof item === 'object' && item !== null) {
      return {
        ID: item.ID || item.id || item.franquia_id || String(item),
        Name: item.Name || item.name || item.franquia_nome || String(item)
      };
    } else {
      return { ID: String(item), Name: String(item) };
    }
  }).filter(item => item.ID && item.Name);
};

const formatCategoriasData = (data: any): SelectOption[] => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((item: any): SelectOption => {
    if (typeof item === 'object' && item !== null) {
      return {
        ID: item.Valor || item.ID || item.id || String(item),
        Name: item.Nome || item.Name || item.name || String(item)
      };
    } else {
      return { ID: String(item), Name: String(item) };
    }
  }).filter(item => item.ID && item.Name);
};

const processRecords = async () => {
  log('DespesasCsvImportModal', 'processRecords', 'Begin');
  const lines = csvContent.value.split('\n').filter(line => line.trim());
  const separator = lines[0].includes(';') ? ';' : ',';

  // Reset skipped count
  skippedPositiveCount.value = 0;

  // Parse all lines first
  const allRecords = lines.slice(1).map((line, index) => {
    const cells = line.split(separator).map(cell => cell.trim().replace(/"/g, ''));

    const valorStr = cells[parseInt(fieldMapping.valor)] || '0';
    const { value, isNegative } = parseValue(valorStr);

    const newRecord: CsvRecord = {
      titulo: cells[parseInt(fieldMapping.titulo)] || '',
      descricao: cells[parseInt(fieldMapping.descricao)] || '',
      valor: value,
      originalValueNegative: isNegative,
      data_vencimento: parseDate(cells[parseInt(fieldMapping.data_vencimento)] || ''),
      franquia_id: defaultFranquiaId.value,
      status_id: '2',
      categoria_id: '',
      qtd_parcelas: 1,
      errors: [],
      isValid: true,
      categorySuggested: false,
      categoryConfidence: 0,
      categoryMatchedWith: '',
      ativoMatches: [],
      selectedAtivoId: null,
      selectedMatch: null,
      confidence: 'NONE',
      checked: false
    };

    return newRecord;
  });

  // Filter: only keep negative values (expenses) - ignore positive values (recebimentos)
  const expenseRecords = allRecords.filter(record => {
    if (!record.originalValueNegative && record.valor > 0) {
      // Positive value = recebimento, skip it
      skippedPositiveCount.value++;
      return false;
    }
    return true;
  });

  log('DespesasCsvImportModal', 'processRecords', 'Filtered records', {
    total: allRecords.length,
    expenses: expenseRecords.length,
    skippedPositive: skippedPositiveCount.value
  });

  // Store records (validation happens later per step)
  processedRecords.value = expenseRecords;

  log('DespesasCsvImportModal', 'processRecords', 'Records ready for reconciliation', {
    total: processedRecords.value.length
  });
};

/**
 * Validate records that will be created (after reconciliation step)
 */
const validateRecordsForCreation = () => {
  log('DespesasCsvImportModal', 'validateRecordsForCreation', 'Begin');

  // Apply default category to records that didn't get a suggestion
  recordsForCreation.value.forEach(record => {
    if (!record.categoria_id && defaultCategoria.value) {
      record.categoria_id = defaultCategoria.value;
    }
  });

  // Validate records for creation
  recordsForCreation.value.forEach(record => {
    record.errors = [];
    record.isValid = true;

    if (!record.titulo) {
      record.errors.push(t('Título obrigatório'));
      record.isValid = false;
    }
    if (record.valor <= 0) {
      record.errors.push(t('Valor inválido'));
      record.isValid = false;
    }
    if (!record.data_vencimento) {
      record.errors.push(t('Data inválida'));
      record.isValid = false;
    }
    if (!record.franquia_id) {
      record.errors.push(t('Franquia obrigatória'));
      record.isValid = false;
    }
    if (!record.categoria_id) {
      record.errors.push(t('Categoria obrigatória'));
      record.isValid = false;
    }
  });

  log('DespesasCsvImportModal', 'validateRecordsForCreation', 'Complete', {
    valid: validRecordsForCreation.value,
    invalid: invalidRecordsForCreation.value
  });
};

/**
 * Find CSV records that match existing Ativo expenses (for reconciliation)
 * Populates ativoMatches array with ALL potential matches for each record
 */
const findMatchesWithAtivoExpenses = async () => {
  log('DespesasCsvImportModal', 'findMatchesWithAtivoExpenses', 'Begin');
  isLoadingAtivoExpenses.value = true;
  matchedRecords.value = [];

  try {
    // Fetch Ativo expenses from last 3 months
    const { dataInicio, dataFim } = getLast3MonthsDateRange();
    const franquiaIds = defaultFranquiaId.value ? [defaultFranquiaId.value] : [];

    log('DespesasCsvImportModal', 'findMatchesWithAtivoExpenses', 'Fetching Ativo expenses', {
      dataInicio,
      dataFim,
      franquiaIds
    });

    const allExpenses = await fetchDespesasWithOverride({
      queryOverride: {
        franquiaIds,
        dataInicio,
        dataFim
      }
    });

    // Filter only Ativo expenses (despesa_status_id = '1')
    // Log first expense structure for debugging
    if (allExpenses.length > 0) {
      log('DespesasCsvImportModal', 'findMatchesWithAtivoExpenses', 'Sample expense structure', {
        keys: Object.keys(allExpenses[0]),
        despesa_status_id: allExpenses[0].despesa_status_id,
        status_id: allExpenses[0].status_id,
        despesa_status: allExpenses[0].despesa_status,
        valor: allExpenses[0].valor,
        despesa_valor: allExpenses[0].despesa_valor
      });
    }

    ativoExpenses.value = allExpenses.filter((exp: DespesaListItem) => {
      // Check multiple possible field names for status
      const statusId = exp.despesa_status_id || exp.status_id || exp.despesa_status;
      const isAtivo = statusId === '1' || statusId === 1 || statusId === 'Ativo';
      return isAtivo;
    });

    log('DespesasCsvImportModal', 'findMatchesWithAtivoExpenses', 'Ativo expenses found', {
      total: allExpenses.length,
      ativo: ativoExpenses.value.length,
      sampleStatuses: allExpenses.slice(0, 5).map((e: DespesaListItem) => ({
        id: e.despesa_id,
        status_id: e.despesa_status_id || e.status_id || e.despesa_status,
        valor: e.valor || e.despesa_valor
      }))
    });

    if (ativoExpenses.value.length === 0) {
      log('DespesasCsvImportModal', 'findMatchesWithAtivoExpenses', 'No Ativo expenses to match');
      reconciliationComplete.value = true;
      return;
    }

    // For each CSV record, find ALL potential Ativo matches
    // Pattern matches RecebimentosCsvImportModal: auto-check EXACT and HIGH confidence
    for (const record of processedRecords.value) {
      const allMatches = findAllMatchingAtivoExpenses(record, ativoExpenses.value);
      record.ativoMatches = allMatches;

      // If there are matches, auto-select the best one
      if (allMatches.length > 0) {
        const bestMatch = allMatches[0]; // Already sorted by confidence
        record.selectedAtivoId = bestMatch.expenseId;
        record.selectedMatch = bestMatch; // Store the match object (like Recebimentos)
        record.confidence = bestMatch.confidenceLevel; // Use confidence field

        // Auto-check EXACT and HIGH confidence matches (like RecebimentosCsvImportModal)
        if (bestMatch.confidenceLevel === 'EXACT' || bestMatch.confidenceLevel === 'HIGH') {
          record.checked = true; // Auto-select high confidence matches
        } else {
          record.checked = false; // MEDIUM, NONE need manual selection
        }

        matchedRecords.value.push({
          csvRecord: record,
          existingExpense: bestMatch.expense,
          confidence: bestMatch.confidence,
          confirmed: record.checked
        });

        log('DespesasCsvImportModal', 'findMatchesWithAtivoExpenses', 'Match found', {
          csvTitulo: record.titulo,
          csvValor: record.valor,
          matchCount: allMatches.length,
          bestMatch: bestMatch.expenseTitle,
          confidence: bestMatch.confidenceLevel,
          autoChecked: record.checked
        });
      } else {
        record.confidence = 'NONE';
        record.checked = false;
      }
    }

    log('DespesasCsvImportModal', 'findMatchesWithAtivoExpenses', 'Complete', {
      totalCsvRecords: processedRecords.value.length,
      matchedCount: matchedRecords.value.length,
      checkedCount: processedRecords.value.filter(r => r.checked).length,
      uncheckedCount: processedRecords.value.filter(r => !r.checked).length
    });

    reconciliationComplete.value = true;

  } catch (err) {
    log('DespesasCsvImportModal', 'findMatchesWithAtivoExpenses', 'Error', err);
    console.error('Error finding matches:', err);
  } finally {
    isLoadingAtivoExpenses.value = false;
  }
};

/**
 * Find ALL matching Ativo expenses for a CSV record
 * PRIMARY MATCH: Same valor (exact match within tolerance)
 * RANKING: By text similarity (for ordering multiple matches)
 * Returns array sorted by confidence (highest first)
 */
const findAllMatchingAtivoExpenses = (csvRecord: CsvRecord, ativoExpensesList: DespesaListItem[]): AtivoMatch[] => {
  const csvValor = parseFloat(String(csvRecord.valor)) || 0;
  const csvTitle = normalizeText(csvRecord.titulo || '');
  const csvDescricao = normalizeText(csvRecord.descricao || '');
  const matches: AtivoMatch[] = [];

  log('DespesasCsvImportModal', 'findAllMatchingAtivoExpenses', 'Looking for matches', {
    csvValor,
    csvTitle,
    csvDescricao,
    ativoCount: ativoExpensesList.length
  });

  for (const expense of ativoExpensesList) {
    // Get valor from multiple possible field names
    const expValor = parseFloat(expense.valor || expense.despesa_valor || 0);
    const expTitle = normalizeText(expense.titulo || expense.despesa_titulo || '');
    const expDescricao = normalizeText(expense.descricao || expense.despesa_descricao || '');
    const expenseId = String(expense.despesa_id || expense.id || '');
    const expenseTitle = expense.titulo || expense.despesa_titulo || '';

    // PRIMARY MATCH: Same valor (with small tolerance for rounding)
    const valorDiff = Math.abs(csvValor - expValor);
    if (valorDiff > 0.02) {
      continue; // Skip if valor doesn't match
    }

    // RANKING: Calculate text similarity for ordering
    let similarity = 0.5; // Base similarity for valor match
    let confidenceLevel: AtivoMatch['confidenceLevel'] = 'MEDIUM';

    // Check exact title match
    if (csvTitle && expTitle && csvTitle === expTitle && csvTitle.length > 3) {
      similarity = 1.0;
      confidenceLevel = 'EXACT';
    }
    // Check exact descricao match
    else if (csvDescricao && expDescricao && csvDescricao === expDescricao && csvDescricao.length > 3) {
      similarity = 1.0;
      confidenceLevel = 'EXACT';
    }
    // Check if CSV descricao contains expense titulo
    else if (csvDescricao && expTitle && csvDescricao.includes(expTitle)) {
      similarity = 0.9;
      confidenceLevel = 'HIGH';
    }
    // Check if expense titulo contains CSV descricao
    else if (expTitle && csvDescricao && expTitle.includes(csvDescricao)) {
      similarity = 0.9;
      confidenceLevel = 'HIGH';
    }
    // Calculate Jaccard similarity
    else if (csvTitle || csvDescricao) {
      const titleSim = jaccardSimilarity(csvTitle + ' ' + csvDescricao, expTitle + ' ' + expDescricao);
      if (titleSim >= 0.3) {
        similarity = 0.5 + (titleSim * 0.5); // 0.5-1.0 range
        confidenceLevel = titleSim >= 0.6 ? 'HIGH' : 'MEDIUM';
      }
    }

    // VALOR MATCHED - Include this expense
    matches.push({
      expense: expense,
      expenseId: expenseId,
      expenseTitle: expenseTitle,
      expenseValor: expValor,
      confidence: similarity,
      confidenceLevel: confidenceLevel
    });

    log('DespesasCsvImportModal', 'findAllMatchingAtivoExpenses', 'Valor match found', {
      csvValor,
      expValor,
      expenseTitle,
      confidenceLevel
    });
  }

  // Sort by confidence (highest first)
  matches.sort((a, b) => b.confidence - a.confidence);

  log('DespesasCsvImportModal', 'findAllMatchingAtivoExpenses', 'Total matches', matches.length);

  return matches;
};

/**
 * Find a matching Ativo expense for a CSV record
 * Criteria: same valor + similar titulo/descricao
 */
const findMatchingAtivoExpense = (csvRecord: CsvRecord, ativoExpensesList: DespesaListItem[]) => {
  const csvValor = parseFloat(String(csvRecord.valor));
  const csvTitle = normalizeText(csvRecord.titulo || '');
  const csvDescricao = normalizeText(csvRecord.descricao || '');

  for (const expense of ativoExpensesList) {
    const expValor = parseFloat(String(expense.valor));
    const expTitle = normalizeText(expense.titulo || expense.despesa_titulo || '');
    const expDescricao = normalizeText(expense.descricao || expense.despesa_descricao || '');

    // First check: same valor (with small tolerance for rounding)
    if (Math.abs(csvValor - expValor) > 0.01) {
      continue; // Skip if valor doesn't match
    }

    // Second check: similar titulo or descricao
    let similarity = 0;

    // Check if CSV descricao contains expense titulo (common bank statement pattern)
    if (csvDescricao && expTitle && csvDescricao.includes(expTitle)) {
      similarity = 0.9;
    } else if (expTitle && csvDescricao && expTitle.includes(csvDescricao)) {
      similarity = 0.9;
    }
    // Check exact title match
    else if (csvTitle === expTitle && csvTitle.length > 3) {
      similarity = 1.0;
    }
    // Check exact descricao match
    else if (csvDescricao === expDescricao && csvDescricao.length > 3) {
      similarity = 1.0;
    }
    // Check Jaccard similarity
    else {
      const titleSim = jaccardSimilarity(csvTitle + ' ' + csvDescricao, expTitle + ' ' + expDescricao);
      if (titleSim >= 0.5) {
        similarity = titleSim;
      }
    }

    if (similarity >= 0.5) {
      return {
        expense: expense,
        expenseId: expense.despesa_id || expense.id,
        expenseTitle: expense.titulo || expense.despesa_titulo,
        confidence: similarity
      };
    }
  }

  return null;
};

/**
 * Toggle confirmation for a matched record
 */
const toggleMatchConfirmation = (index: number) => {
  if (matchedRecords.value[index]) {
    matchedRecords.value[index].confirmed = !matchedRecords.value[index].confirmed;

    // Update the CSV record's checked status (matches RecebimentosCsvImportModal pattern)
    const csvRecord = matchedRecords.value[index].csvRecord;
    csvRecord.checked = matchedRecords.value[index].confirmed;
  }
};

/**
 * Get CSS class for reconciliation table row based on match state
 * Matches RecebimentosCsvImportModal pattern with opacity for unchecked rows
 * @param {Object} record - CSV record
 * @returns {string} - CSS class name
 */
const getRowClass = (record: CsvRecord): string => {
  if (!record.checked) {
    return 'row-unchecked'; // Opacity 0.5 for unchecked rows
  }

  switch (record.confidence) {
    case 'EXACT':
      return 'row-exact-confidence';
    case 'HIGH':
      return 'row-high-confidence';
    case 'MEDIUM':
      return 'row-medium-confidence';
    default:
      return '';
  }
};

/**
 * Get dropdown options for Ativo match selection
 * Matches RecebimentosCsvImportModal pattern
 * @param {Object} record - CSV record with ativoMatches array
 * @returns {Array} - Array of options for SelectList component
 */
const getMatchOptions = (record: CsvRecord): SelectOption[] => {
  const options: SelectOption[] = [
    { ID: '', Name: t('-- Criar nova despesa --') }
  ];

  // Get IDs of auto-matched expenses to avoid duplicates
  const matchedIds = new Set((record.ativoMatches || []).map(m => m.expenseId));

  // First add auto-matched expenses (highlighted at top)
  if (record.ativoMatches && record.ativoMatches.length > 0) {
    for (const match of record.ativoMatches) {
      const confidenceIcon = match.confidenceLevel === 'EXACT' ? '✓✓' :
                            match.confidenceLevel === 'HIGH' ? '✓' : '~';
      const valorFormatted = n(match.expenseValor, 'currency');
      options.push({
        ID: match.expenseId,
        Name: `${confidenceIcon} ${match.expenseTitle} (${valorFormatted})`
      });
    }

    // Add separator if there are more expenses
    if (ativoExpenses.value.length > matchedIds.size) {
      options.push({ ID: '__separator__', Name: '─── ' + t('Outras despesas ativas') + ' ───', disabled: true });
    }
  }

  // Then add ALL other Ativo expenses (not auto-matched)
  for (const expense of ativoExpenses.value) {
    const expenseId = expense.despesa_id || expense.id;
    if (!matchedIds.has(expenseId)) {
      const expenseTitle = expense.titulo || expense.despesa_titulo || t('Sem título');
      const expenseValor = Number(expense.valor || 0);
      const valorFormatted = n(expenseValor, 'currency');
      const dateStr = expense.data_vencimento || expense.despesa_data_vencimento || '';
      const dateFormatted = dateStr ? formatDateForDisplay(dateStr) : '';
      options.push({
        ID: expenseId,
        Name: `${expenseTitle} (${valorFormatted}) ${dateFormatted}`
      });
    }
  }

  return options;
};

/**
 * Handle selection change in Ativo match dropdown
 * Matches RecebimentosCsvImportModal pattern - uses checked and confidence
 * @param {Object} record - CSV record being edited
 * @param {string} selectedId - Selected expense ID
 */
const handleMatchSelection = (record: CsvRecord, selectedId: string | null) => {
  log('DespesasCsvImportModal', 'handleMatchSelection', 'Selection changed', {
    recordTitulo: record.titulo,
    selectedId
  });

  // Skip separator option
  if (selectedId === '__separator__') {
    return;
  }

  record.selectedAtivoId = selectedId;

  if (selectedId) {
    // First check if it's an auto-matched expense
    const selectedMatch = record.ativoMatches?.find(m => m.expenseId === selectedId);

    if (selectedMatch) {
      // Auto-matched expense - use its confidence level
      record.confidence = selectedMatch.confidenceLevel;
      record.selectedMatch = selectedMatch;
      // Auto-check EXACT and HIGH confidence
      record.checked = selectedMatch.confidenceLevel === 'EXACT' || selectedMatch.confidenceLevel === 'HIGH';
    } else {
      // Manual selection from all Ativo expenses
      const expense = ativoExpenses.value.find(e =>
        (e.despesa_id || e.id) === selectedId
      );
      if (expense) {
        record.confidence = 'MEDIUM'; // Manual selection treated as MEDIUM confidence
        record.selectedMatch = {
          expense: expense,
          expenseId: selectedId,
          expenseTitle: expense.titulo || expense.despesa_titulo || '',
          expenseValor: parseFloat(String(expense.valor || expense.despesa_valor || 0)),
          confidence: 0.5,
          confidenceLevel: 'MEDIUM'
        };
        record.checked = true; // User manually selected, so mark as checked
      }
    }
  } else {
    // No selection = will create new
    record.confidence = 'NONE';
    record.selectedMatch = null;
    record.checked = false;
  }
};

/**
 * Toggle checked state for a record
 * Matches RecebimentosCsvImportModal pattern
 * @param {Object} record - CSV record
 */
const toggleRowChecked = (record: CsvRecord) => {
  if (!record.selectedAtivoId) {
    return; // Can't toggle without a selection
  }

  record.checked = !record.checked;

  log('DespesasCsvImportModal', 'toggleRowChecked', 'Toggled', {
    recordTitulo: record.titulo,
    checked: record.checked
  });
};

/**
 * Parse a value string and return both the absolute value and whether it was negative
 */
const parseValue = (valueStr: string): { value: number; isNegative: boolean } => {
  // Remove currency symbols and whitespace
  let cleaned = valueStr.replace(/[R$\s]/g, '');

  // Handle negative sign
  const isNegative = cleaned.startsWith('-');
  if (isNegative) {
    cleaned = cleaned.substring(1);
  }

  // Brazilian format: 1.190,00 -> thousands separator = period, decimal separator = comma
  // Remove thousands separator (period) and replace decimal separator (comma) with period
  cleaned = cleaned.replace(/\./g, '').replace(',', '.');

  const value = parseFloat(cleaned) || 0;

  // Return both value (always positive) and whether original was negative
  return {
    value: Math.abs(value),
    isNegative
  };
};

const parseDate = (dateStr: string): string => {
  // Try to parse various date formats
  const formats = [
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, // DD/MM/YYYY
    /^(\d{4})-(\d{1,2})-(\d{1,2})$/, // YYYY-MM-DD
    /^(\d{1,2})-(\d{1,2})-(\d{4})$/ // DD-MM-YYYY
  ];

  for (let format of formats) {
    const match = dateStr.match(format);
    if (match) {
      if (format === formats[0] || format === formats[2]) {
        // DD/MM/YYYY or DD-MM-YYYY
        return `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
      } else {
        // YYYY-MM-DD
        return `${match[1]}-${match[2].padStart(2, '0')}-${match[3].padStart(2, '0')}`;
      }
    }
  }

  return '';
};

/**
 * Format a date string (YYYY-MM-DD) for display using i18n locale
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @returns {string} - Formatted date string (e.g., "02/12/2025" for pt-BR)
 */
const formatDateForDisplay = (dateStr: string): string => {
  if (!dateStr) return '-';

  try {
    // Parse the YYYY-MM-DD format
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return dateStr;

    const date = new Date(year, month - 1, day);

    // Use i18n's d() function for locale-aware formatting
    return d(date, 'short');
  } catch {
    // Fallback: return the original string if parsing fails
    return dateStr;
  }
};

// =============================================================================
// AUTO-CATEGORIZATION: Similarity matching utilities
// =============================================================================

/**
 * Normalize text for comparison:
 * - Remove accents (é → e)
 * - Lowercase
 * - Remove special characters
 * - Collapse whitespace
 */
const normalizeText = (text: string): string => {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9\s]/g, ' ')    // Replace special chars with space
    .replace(/\s+/g, ' ')            // Collapse multiple spaces
    .trim();
};

/**
 * Get words from text as a Set (for Jaccard similarity)
 */
const getWords = (text: string): Set<string> => {
  const normalized = normalizeText(text);
  if (!normalized) return new Set();
  return new Set(normalized.split(' ').filter(w => w.length > 1));
};

/**
 * Calculate Jaccard similarity between two texts
 * Returns value between 0 (no overlap) and 1 (identical)
 */
const jaccardSimilarity = (textA: string, textB: string): number => {
  const setA = getWords(textA);
  const setB = getWords(textB);

  if (setA.size === 0 || setB.size === 0) return 0;

  const intersection = [...setA].filter(word => setB.has(word));
  const unionSize = new Set([...setA, ...setB]).size;

  return intersection.length / unionSize;
};

/**
 * Check if one text contains the other (after normalization)
 */
const containsMatch = (textA: string, textB: string): boolean => {
  const normA = normalizeText(textA);
  const normB = normalizeText(textB);

  if (!normA || !normB) return false;

  // Check if shorter is contained in longer
  return normA.includes(normB) || normB.includes(normA);
};

/**
 * Find category ID by category name from the categories options
 * @param {string} categoryName - Category name to look up
 * @returns {string|null} - Category ID or null
 */
const findCategoryIdByName = (categoryName: string): string | null => {
  if (!categoryName || categoriasOptions.value.length === 0) return null;

  const normalizedName = normalizeText(categoryName);

  // Try exact match first
  const exactMatch = categoriasOptions.value.find(cat =>
    normalizeText(cat.Name) === normalizedName
  );
  if (exactMatch) return exactMatch.ID;

  // Try contains match (for cases like "Produtos" matching "Produtos + frete")
  const containsMatchCat = categoriasOptions.value.find(cat => {
    const catName = normalizeText(cat.Name);
    return catName.includes(normalizedName) || normalizedName.includes(catName);
  });
  if (containsMatchCat) return containsMatchCat.ID;

  return null;
};

/**
 * Find the best category match from historical expenses
 * Uses multiple strategies: exact match, substring, Jaccard similarity
 * Compares individual fields (titulo, descricao) for better matching
 *
 * @param {string} inputTitle - Title from CSV record
 * @param {string} inputDescricao - Description from CSV record
 * @param {Array} historicalExpenses - Array of existing expenses
 * @returns {Object|null} - { categoryId, confidence, matchedWith } or null
 */
const findBestCategoryMatch = (inputTitle: string, inputDescricao: string, historicalExpenses: DespesaListItem[]): CategoryMatch | null => {
  if (!historicalExpenses || historicalExpenses.length === 0) return null;

  const normalizedInputTitle = normalizeText(inputTitle || '');
  const normalizedInputDescricao = normalizeText(inputDescricao || '');
  const inputTexts = [normalizedInputTitle, normalizedInputDescricao].filter(t => t);

  if (inputTexts.length === 0) return null;

  let bestMatch: CategoryMatch | null = null;

  for (const expense of historicalExpenses) {
    const expTitle = expense.despesa_titulo || expense.titulo || '';
    const expDescricao = expense.despesa_descricao || expense.descricao || '';
    const normalizedExpTitle = normalizeText(expTitle);
    const normalizedExpDescricao = normalizeText(expDescricao);
    const expTexts = [normalizedExpTitle, normalizedExpDescricao].filter(t => t);

    // Get category - try ID first, then look up by name
    let categoryId = expense.despesa_categoria_id || expense.categoria_id;
    if (!categoryId) {
      // API returns category name, not ID - look it up
      const categoryName = expense.categoria || expense.categoria_raw;
      categoryId = findCategoryIdByName(categoryName);
    }

    if (expTexts.length === 0 || !categoryId) continue;

    // Strategy 1: Exact match on any field (100% confidence)
    for (const inputText of inputTexts) {
      for (const expText of expTexts) {
        if (inputText === expText && inputText.length > 3) {
          log('DespesasCsvImportModal', 'findBestCategoryMatch', 'Exact match found', {
            inputText,
            expText,
            expTitle,
            categoryId
          });
          return {
            categoryId: categoryId,
            confidence: 1.0,
            matchedWith: expTitle
          };
        }
      }
    }

    // Strategy 2: Contains match on any field (80% confidence)
    // Check if CSV descricao contains historical titulo (common pattern)
    for (const inputText of inputTexts) {
      for (const expText of expTexts) {
        if (inputText.length > 5 && expText.length > 5) {
          if (inputText.includes(expText) || expText.includes(inputText)) {
            const score = 0.85;
            if (!bestMatch || score > bestMatch.confidence) {
              log('DespesasCsvImportModal', 'findBestCategoryMatch', 'Contains match found', {
                inputText,
                expText,
                expTitle,
                categoryId,
                score
              });
              bestMatch = {
                categoryId: categoryId,
                confidence: score,
                matchedWith: expTitle
              };
            }
          }
        }
      }
    }

    // Strategy 3: Jaccard similarity across all field combinations
    if (!bestMatch || bestMatch.confidence < 0.8) {
      for (const inputText of inputTexts) {
        for (const expText of expTexts) {
          const jaccard = jaccardSimilarity(inputText, expText);
          if (jaccard >= 0.5 && (!bestMatch || jaccard > bestMatch.confidence)) {
            log('DespesasCsvImportModal', 'findBestCategoryMatch', 'Jaccard match found', {
              inputText,
              expText,
              expTitle,
              categoryId,
              jaccard
            });
            bestMatch = {
              categoryId: categoryId,
              confidence: jaccard,
              matchedWith: expTitle
            };
          }
        }
      }
    }
  }

  // Only return if confidence is above threshold
  return bestMatch && bestMatch.confidence >= 0.5 ? bestMatch : null;
};

// Store historical expenses for category suggestion
const historicalExpenses = ref<DespesaListItem[]>([]);
const isSuggestingCategories = ref<boolean>(false);

/**
 * Get date range for last 3 months (for historical expense lookup)
 */
const getLast3MonthsDateRange = (): { dataInicio: string; dataFim: string } => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 3);

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    dataInicio: formatDate(startDate),
    dataFim: formatDate(endDate)
  };
};

/**
 * Fetch historical expenses and suggest categories for processed records
 * Uses X-Query headers to fetch expenses from the last 3 months
 */
const suggestCategoriesFromHistory = async (records: CsvRecord[]): Promise<number> => {
  log('DespesasCsvImportModal', 'suggestCategoriesFromHistory', 'Begin', { recordCount: records.length });
  isSuggestingCategories.value = true;

  try {
    // Fetch historical expenses if not already loaded
    if (historicalExpenses.value.length === 0) {
      // Get last 3 months date range
      const { dataInicio, dataFim } = getLast3MonthsDateRange();

      // Get franchise ID from the default selection
      const franquiaIds = defaultFranquiaId.value ? [defaultFranquiaId.value] : [];

      log('DespesasCsvImportModal', 'suggestCategoriesFromHistory', 'Fetching historical expenses', {
        dataInicio,
        dataFim,
        franquiaIds
      });

      // Use fetchDespesasWithOverride to get expenses from last 3 months
      historicalExpenses.value = await fetchDespesasWithOverride({
        queryOverride: {
          franquiaIds,
          dataInicio,
          dataFim
        }
      });

      log('DespesasCsvImportModal', 'suggestCategoriesFromHistory', 'Loaded historical expenses', {
        count: historicalExpenses.value.length,
        dateRange: `${dataInicio} to ${dataFim}`
      });

      // DEBUG: Log sample expense structure to verify field names
      if (historicalExpenses.value.length > 0) {
        const sample = historicalExpenses.value[0];
        log('DespesasCsvImportModal', 'suggestCategoriesFromHistory', 'Sample expense structure', {
          keys: Object.keys(sample),
          titulo: sample.despesa_titulo || sample.titulo || 'NOT FOUND',
          descricao: sample.despesa_descricao || sample.descricao || 'NOT FOUND',
          categoria_id: sample.despesa_categoria_id || sample.categoria_id || 'NOT FOUND',
          raw: sample
        });

        // Count how many have categories
        const withCategory = historicalExpenses.value.filter(e =>
          e.despesa_categoria_id || e.categoria_id
        ).length;
        log('DespesasCsvImportModal', 'suggestCategoriesFromHistory', 'Expenses with categories', {
          withCategory,
          total: historicalExpenses.value.length,
          percentage: Math.round((withCategory / historicalExpenses.value.length) * 100) + '%'
        });
      }
    }

    let suggestedCount = 0;

    // For each record, try to find a category match
    for (const record of records) {
      log('DespesasCsvImportModal', 'suggestCategoriesFromHistory', 'Looking for match', {
        titulo: record.titulo,
        descricao: record.descricao,
        normalizedInput: normalizeText(`${record.titulo} ${record.descricao}`)
      });

      const match = findBestCategoryMatch(
        record.titulo,
        record.descricao,
        historicalExpenses.value
      );

      log('DespesasCsvImportModal', 'suggestCategoriesFromHistory', 'Match result', {
        titulo: record.titulo,
        matchFound: !!match,
        match: match
      });

      if (match) {
        record.categoria_id = match.categoryId;
        record.categorySuggested = true;
        record.categoryConfidence = match.confidence;
        record.categoryMatchedWith = match.matchedWith;
        suggestedCount++;

        log('DespesasCsvImportModal', 'suggestCategoriesFromHistory', 'Suggested category', {
          titulo: record.titulo,
          categoryId: match.categoryId,
          confidence: Math.round(match.confidence * 100) + '%',
          matchedWith: match.matchedWith
        });
      }
    }

    log('DespesasCsvImportModal', 'suggestCategoriesFromHistory', 'Complete', {
      suggestedCount,
      totalRecords: records.length
    });

    return suggestedCount;

  } catch (err) {
    log('DespesasCsvImportModal', 'suggestCategoriesFromHistory', 'Error', err);
    console.error('Error suggesting categories:', err);
    return 0;
  } finally {
    isSuggestingCategories.value = false;
  }
};

const applyDefaults = () => {
  processedRecords.value.forEach(record => {
    if (defaultStatus.value) record.status_id = defaultStatus.value;
    if (defaultCategoria.value) record.categoria_id = defaultCategoria.value;
    if (defaultQtdParcelas.value) record.qtd_parcelas = defaultQtdParcelas.value;
  });
};

const startImport = async () => {
  log('DespesasCsvImportModal', 'startImport', 'Begin');

  // Get records for reconciliation (confirmed matches to baixar)
  const recordsToBaixar = recordsForReconciliation.value;
  // Get valid records for creation
  const validNewRecords = recordsForCreation.value.filter(record => record.isValid);

  const totalOperations = recordsToBaixar.length + validNewRecords.length;

  Object.assign(importStats, { processed: 0, success: 0, errors: 0, reconciled: 0 });
  importProgress.value = 0;
  importComplete.value = false;

  log('DespesasCsvImportModal', 'startImport', 'Operations', {
    toBaixar: recordsToBaixar.length,
    toCreate: validNewRecords.length,
    total: totalOperations
  });

  // Phase 1: Reconciliation - Baixar existing expenses
  for (let i = 0; i < recordsToBaixar.length; i++) {
    const match = recordsToBaixar[i];
    const existingExpense = match.existingExpense as DespesaListItem;
    const expenseId = existingExpense.despesa_id || existingExpense.id;

    try {
      log('DespesasCsvImportModal', 'startImport', 'Baixando expense', {
        id: expenseId,
        titulo: existingExpense.titulo || existingExpense.despesa_titulo
      });

      await baixarDespesa(expenseId);

      importStats.reconciled++;
      importStats.success++;

    } catch (error) {
      console.error('[DespesasCsvImportModal] Error baixando expense:', error);
      importStats.errors++;
    }

    importStats.processed++;
    if (totalOperations > 0) {
      importProgress.value = Math.round((importStats.processed / totalOperations) * 100);
    }
  }

  // Phase 2: Create new expenses
  for (let i = 0; i < validNewRecords.length; i++) {
    const record = validNewRecords[i];

    try {
      await saveDespesaAPI({
        despesa_titulo: record.titulo,
        despesa_descricao: record.descricao,
        despesa_valor: record.valor,
        despesa_data_vencimento: record.data_vencimento,
        despesa_franquia_id: Number(record.franquia_id),
        despesa_status: Number(record.status_id),
        despesa_categoria_id: Number(record.categoria_id),
        despesa_qtd_parcelas: record.qtd_parcelas,
        despesa_data_lancamento: new Date().toISOString().slice(0, 19).replace('T', ' ')
      });

      importStats.success++;

    } catch (error) {
      console.error('[DespesasCsvImportModal] Error importing record:', error);
      importStats.errors++;
    }

    importStats.processed++;
    if (totalOperations > 0) {
      importProgress.value = Math.round((importStats.processed / totalOperations) * 100);
    }
  }

  importComplete.value = true;

  // Build completion message
  let message = t('Importação concluída!') + ' ';
  if (importStats.reconciled > 0) {
    message += `${importStats.reconciled} ` + t('baixados,') + ' ';
  }
  const newCreated = importStats.success - importStats.reconciled;
  if (newCreated > 0) {
    message += `${newCreated} ` + t('criados,') + ' ';
  }
  if (importStats.errors > 0) {
    message += `${importStats.errors} ` + t('erros.');
  } else {
    message = message.slice(0, -2) + '.'; // Remove trailing comma and space
  }

  eventBus.emit('globalMessage.show', message);
};

// Standard modal open handler
const handleModalOpen = (data?: unknown) => {
  log('DespesasCsvImportModal', 'handleModalOpen', 'Standard modal open event received', data);
  openModal();
};

// Lifecycle hooks
onMounted(() => {
  log('DespesasCsvImportModal', 'mounted', 'Setting up event listeners');

  // Standard modal events (preferred pattern)
  eventBus.on('modal.despesas-csv-import.open', handleModalOpen);
  eventBus.on('modal.despesas-csv-import.close', closeModal);

  // Load options immediately
  loadOptions();

  // Also listen for when permissions are ready
  eventBus.on('permissions.ready', () => {
    log('DespesasCsvImportModal', 'event:permissions.ready', 'Received');
    loadOptions();
  });
});

onBeforeUnmount(() => {
  log('DespesasCsvImportModal', 'onBeforeUnmount', 'Cleaning up event listeners');

  // Remove standard modal events
  eventBus.off('modal.despesas-csv-import.open', handleModalOpen);
  eventBus.off('modal.despesas-csv-import.close', closeModal);

  // Clean up other event listeners
  eventBus.off('permissions.ready', () => {});
});

// Expose public methods for parent components
defineExpose({
  openModal,
  closeModal
});
</script>

<style scoped>
.progress {
  margin: 15px 0;
}

.info-box {
  display: block;
  min-height: 90px;
  background: #fff;
  width: 100%;
  box-shadow: 0 1px 1px rgba(0,0,0,0.1);
  border-radius: 2px;
  margin-bottom: 15px;
}

.info-box-icon {
  border-top-left-radius: 2px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 2px;
  display: block;
  float: left;
  height: 90px;
  width: 90px;
  text-align: center;
  font-size: 45px;
  line-height: 90px;
  background: rgba(0,0,0,0.2);
}

.info-box-content {
  padding: 5px 10px;
  margin-left: 90px;
}

.info-box-text {
  text-transform: uppercase;
  font-weight: bold;
  font-size: 12px;
}

.info-box-number {
  display: block;
  font-weight: bold;
  font-size: 18px;
}

.bg-green { background-color: #00a65a !important; color: white; }
.bg-red { background-color: #dd4b39 !important; color: white; }
.bg-blue { background-color: #3c8dbc !important; color: white; }

.panel {
  margin-bottom: 20px;
  background-color: #fff;
  border: 1px solid transparent;
  border-radius: 4px;
  box-shadow: 0 1px 1px rgba(0,0,0,.05);
}

.panel-default {
  border-color: #ddd;
}

.panel-heading {
  padding: 10px 15px;
  border-bottom: 1px solid transparent;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  color: #333;
  background-color: #f5f5f5;
  border-color: #ddd;
}

.panel-body {
  padding: 15px;
}

.panel-title {
  margin-top: 0;
  margin-bottom: 0;
  font-size: 16px;
}

.table-responsive {
  overflow-x: auto;
}

.text-right {
  text-align: right;
}

.mt-3 {
  margin-top: 1rem;
}

/* Reconciliation table row styles */
.table-info {
  background-color: rgba(0, 188, 212, 0.15) !important;
}

.table-success {
  background-color: rgba(76, 175, 80, 0.15) !important;
}

.table-warning {
  background-color: rgba(255, 193, 7, 0.15) !important;
}

.table-primary {
  background-color: rgba(33, 150, 243, 0.15) !important;
}

.description-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.description-cell div {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.description-cell small {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Row styling for Conciliação step - matches RecebimentosCsvImportModal pattern */
.row-unchecked {
  background-color: var(--surface-100) !important;
  opacity: 0.5;
}

.row-exact-confidence {
  background-color: rgba(0, 188, 212, 0.15) !important;
}

.row-high-confidence {
  background-color: rgba(76, 175, 80, 0.15) !important;
}

.row-medium-confidence {
  background-color: rgba(255, 193, 7, 0.15) !important;
}
</style>
